// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyOrder, TyOrderItem, TyOrderStatus, TyOrderType } from "@/types";
import OrderTimeline from "@/UI/components/OrderTimeline";
import PhoneInput from "@/UI/inputs/PhoneInput";
import { formatPhone } from "@/UI/inputs/PhoneInput/formatPhone";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import OrderItemPickerPopup from "@/UI/popups/OrderItemPickerPopup";
import { pickLangValue, readLangs } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    onClose: () => void;
    details?: {
        /** Absent when the counter is writing down a new order rather than correcting one. */
        order?: TyOrder;
        onSaved: (order: TyOrder, isNew: boolean) => void;
    };
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * Correcting an order a store took down wrong — the number read back over a bad line, the wrong
 * table, a delivery the customer turned into a pickup at the door.
 *
 * The address field stays mounted whatever the type is, because the server keeps the address through
 * a type change: an order switched by mistake has to be switchable back with everything still on it.
 * It is simply not asked for, and not shown on the card, while the type does not use it.
 *
 * The same form writes a new one down. A café taking an order over the phone is filling in exactly
 * these fields, and a second form that happened to look like this one would drift from it by the
 * second release.
 *
 * Nothing here decides a price. Lines are sent as product, tier and quantity, and the API prices them
 * from the catalogue exactly as it did when the order was placed — the total below is this form's own
 * arithmetic on what it last heard, which is why it is labelled an estimate.
 *
 * **Status is applied the moment it is picked**, not on save: it is applied that way from the card and
 * from the preview too, and a status waiting behind a save button would be a different promise from
 * the same control in two other places. Everything else on the form is saved together, once.
 */
export default function OrderEditPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { addComp, removeComp } = usePopup();

    const order = details?.order;
    const isNew = !order;

    const [phone, setPhone] = useState<string | undefined>(order?.customerPhone);
    const [type, setType] = useState<TyOrderType>(order?.type ?? "pickup");
    const [tableNumber, setTableNumber] = useState(order?.tableNumber ?? "");
    const [address, setAddress] = useState(order?.address ?? "");
    const [items, setItems] = useState<TyOrderItem[]>(order?.items ?? []);
    /** Mirrors what the server last said, so the trail redraws without reopening the form. */
    const [status, setStatus] = useState<TyOrderStatus>(order?.status ?? "pending");

    const {
        data: saveData,
        loading: saving,
        error,
        invoke: save,
    } = useFetch<TyApiResponse<{ order: TyOrder }>>(isNew ? "/orders" : `/orders/${order!._id}`, { method: isNew ? "POST" : "PUT" }, false);

    const {
        data: statusData,
        loading: movingStatus,
        error: statusError,
        invoke: move,
    } = useFetch<TyApiResponse<{ order: TyOrder }>>(`/orders/${order?._id ?? "x"}/status`, { method: "PUT" }, false);

    // `invoke` only starts the request — the answer arrives here, the same way every form in the app
    // reads one. Awaiting the call itself would hand back `undefined` and silently never close.
    useEffect(() => {
        if (!saveData?.success) return;
        details?.onSaved(saveData.data.order, isNew);
        onClose();
    }, [saveData]);

    /** A status is applied on its own, so the form stays open on the order it just moved. */
    useEffect(() => {
        const updated = statusData?.data?.order;
        if (!updated) return;
        setStatus(updated.status);
        details?.onSaved(updated, false);
    }, [statusData]);

    /* Lines compare by what the API is actually told — tier and count. A name or a price that moved
       in the catalogue since the order was placed is not an edit the store made. */
    const itemsKey = (list: TyOrderItem[]) => list.map((item) => `${item.product}:${item.priceId}:${item.quantity}`).join("|");
    const itemsDirty = itemsKey(items) !== itemsKey(order?.items ?? []);

    const dirty =
        isNew ||
        (phone ?? "") !== order!.customerPhone ||
        type !== order!.type ||
        tableNumber.trim() !== (order!.tableNumber ?? "") ||
        address.trim() !== (order!.address ?? "") ||
        itemsDirty;

    const missing = !phone || (type === "table" && !tableNumber.trim()) || (type === "delivery" && !address.trim()) || items.length === 0;

    // What it comes to on today's prices. The API re-prices on save, and its number is the one kept.
    const estimate = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

    /** This kind's own path plus the two ways to call an order off — never a flat list of all eight. */
    const statusOptions = [...(constants.orderStatusFlow[type] ?? []), ...constants.orderAbortStatuses].map((entry) => ({
        value: entry,
        label: t.orders.status[entry as TyOrderStatus],
    }));

    // --- Handlers --------------------------------------------------------------------------------

    const handleQuantity = (index: number, by: number) => {
        setItems((prev) =>
            prev.map((item, at) =>
                at === index ? { ...item, quantity: Math.min(constants.maxOrderItemQuantity, Math.max(1, item.quantity + by)) } : item
            )
        );
    };

    const handleRemove = (index: number) => setItems((prev) => prev.filter((_item, at) => at !== index));

    /* The whole list goes over to the picker and the whole list comes back: in there it is a draft,
       and a cancelled draft has to leave the order exactly as it was found. */
    const handleAddItem = () => {
        removeComp("order-item-picker");
        addComp({
            name: "order-item-picker",
            Component: OrderItemPickerPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: { items, branch: order?.branch?._id ?? null, onApply: (next: TyOrderItem[]) => setItems(next) },
        });
    };

    /** Confirmed like every other status change in the app — the customer sees what happens here. */
    const handleStatus = (next: TyOrderStatus) => {
        if (!order || next === status) return;
        const isAbort = constants.orderAbortStatuses.includes(next as "rejected" | "cancelled");

        removeComp("confirm-order-status");
        addComp({
            name: "confirm-order-status",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message: t.orders.movePrompt.replace("{status}", t.orders.status[next]),
                icon: isAbort ? "/icons/cancel-icon.svg" : "/icons/check-icon.svg",
                variant: isAbort ? "danger" : "primary",
                confirmLabel: isAbort ? t.orders.cancelLabel : t.orders.moveLabel,
                cancelLabel: t.orders.editCancelLabel,
                onConfirm: () => move({ method: "PUT", data: { status: next } }),
            },
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!dirty || missing || saving) return;

        const lines = items.map((item) => ({ product: item.product, priceId: item.priceId, quantity: item.quantity }));
        const body = {
            customerPhone: formatPhone(phone),
            type,
            tableNumber: tableNumber.trim() || null,
            address: address.trim() || null,
            /* On an edit they are left out entirely when untouched: sending them asks the API to
               re-price the order, which would restate a line the catalogue has moved under. */
            ...(isNew || itemsDirty ? { items: lines } : {}),
        };

        // A new order needs no confirming — nobody is waiting on it yet.
        if (isNew) return save({ method: "POST", data: body });

        // An edit lands on an order a customer is already waiting on, so it is confirmed first.
        removeComp("confirm-order-edit");
        addComp({
            name: "confirm-order-edit",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message: t.orders.editPrompt.replace("{number}", String(order!.orderNumber)),
                icon: "/icons/check-icon.svg",
                variant: "primary",
                confirmLabel: t.orders.editConfirmLabel,
                cancelLabel: t.orders.editCancelLabel,
                onConfirm: () => save({ method: "PUT", data: body }),
            },
        });
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <div className="order-edit-popup">
            <div className="order-edit-popup-header">
                <p className="order-edit-popup-title">{isNew ? t.orders.newTitle : `${t.orders.editTitle} #${order!.orderNumber}`}</p>
                <button type="button" className="order-edit-popup-close" onClick={onClose} aria-label={t.orders.editCancelLabel}>
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            <form className="oep-body" onSubmit={handleSubmit} noValidate>
                <div className="oep-fields">
                    {/* Where it has got to, and the one control that moves it. Both read the order's own
                    kind, so a delivery is never offered a step a delivery does not have. */}
                    {!isNew && (
                        <div className="oep-status">
                            <OrderTimeline type={type} status={status} />
                            <SelectInput
                                value={status}
                                onChange={(next) => handleStatus(next as TyOrderStatus)}
                                options={statusOptions}
                                disabled={movingStatus}
                                placeholder={t.orders.statusLabel}
                            />
                            {statusError && <p className="oep-error">{statusError}</p>}
                        </div>
                    )}

                    <label className="oep-field">
                        <span className="oep-label">{t.orders.phoneLabel}</span>
                        <PhoneInput value={phone} onChange={setPhone} placeholder={t.orders.phoneLabel} disabled={saving} />
                    </label>

                    <label className="oep-field">
                        <span className="oep-label">{t.orders.typeLabel}</span>
                        <SelectInput
                            value={type}
                            onChange={(next) => setType(next as TyOrderType)}
                            options={constants.orderTypes.map((entry) => ({ value: entry, label: t.orders.type[entry] ?? entry }))}
                            disabled={saving}
                        />
                    </label>

                    {type === "table" && (
                        <label className="oep-field">
                            <span className="oep-label">{t.orders.tableLabel}</span>
                            <TextInput value={tableNumber} onChange={setTableNumber} placeholder={t.orders.tableLabel} disabled={saving} />
                        </label>
                    )}

                    {type === "delivery" && (
                        <label className="oep-field">
                            <span className="oep-label">{t.orders.addressLabel}</span>
                            <TextInput value={address} onChange={setAddress} placeholder={t.orders.addressLabel} disabled={saving} />
                        </label>
                    )}

                    <div className="oep-items">
                        <div className="oep-items-head">
                            <p className="oep-items-title">{t.orders.itemsTitle}</p>
                            <button type="button" className="oep-items-add" onClick={handleAddItem}>
                                + {t.orders.addItemLabel}
                            </button>
                        </div>

                        {items.map((item, index) => (
                            <div className="oep-item" key={`${item.product}:${item.priceId}`}>
                                <span className="oep-item-text">
                                    <span className="oep-item-name">{pickLangValue(readLangs(item.name, ["ar", "en"]), lang)}</span>
                                    {pickLangValue(readLangs(item.label, ["ar", "en"]), lang) && (
                                        <span className="oep-item-label">{pickLangValue(readLangs(item.label, ["ar", "en"]), lang)}</span>
                                    )}
                                </span>

                                <span className="oep-item-steps">
                                    <button
                                        type="button"
                                        className="oep-item-step"
                                        onClick={() => handleQuantity(index, -1)}
                                        disabled={item.quantity <= 1}
                                    >
                                        −
                                    </button>
                                    <span className="oep-item-count">{item.quantity}</span>
                                    <button
                                        type="button"
                                        className="oep-item-step"
                                        onClick={() => handleQuantity(index, 1)}
                                        disabled={item.quantity >= constants.maxOrderItemQuantity}
                                    >
                                        +
                                    </button>
                                </span>

                                <span className="oep-item-price">{item.price * item.quantity}</span>

                                <button
                                    type="button"
                                    className="oep-item-remove"
                                    onClick={() => handleRemove(index)}
                                    aria-label={t.orders.removeItemLabel}
                                    title={t.orders.removeItemLabel}
                                >
                                    <img src="/icons/close-icon.svg" alt="" width={12} height={12} />
                                </button>
                            </div>
                        ))}

                        {items.length === 0 && <p className="oep-items-empty">{t.orders.itemsRequired}</p>}

                        <div className="oep-items-total">
                            <span>{t.orders.estimateLabel}</span>
                            <span className="oep-items-total-value">{estimate}</span>
                        </div>
                    </div>

                    {error && <p className="oep-error">{error}</p>}
                </div>

                <div className="oep-actions">
                    <button type="button" className="oep-btn oep-btn--ghost" onClick={onClose} disabled={saving}>
                        {t.orders.editCancelLabel}
                    </button>
                    <button type="submit" className="oep-btn oep-btn--primary" disabled={!dirty || missing || saving}>
                        {saving ? t.orders.editSavingLabel : isNew ? t.orders.createLabel : t.orders.editSaveLabel}
                    </button>
                </div>
            </form>
        </div>
    );
}
